import { useRef, useState, useContext } from 'react';

import styles from './AuthenticationDialog.module.css';
import config from '../../../config';
import { TrackingContext as TrackerContext } from '../../../tracking/Tracking.context';
import {
	authenticateViaJwt,
	LoginArgs,
	AuthenticateUserResponse,
	authenticateViaLogin,
	useOnClickOutside,
	SignUpProps,
} from './Authentication.hooks';

import { SignUpCognito } from './AuthenticationCognito';

const validTypes = new Set(['login', 'jwt', 'cognito']);

export const AuthenticationDialog = ({
	isOpen,
	mode,
	setMode,
	onClickSignUp,
	onClickLogIn,
	onCloseDialog,
	onSuccess,
}: DialogProps) => {
	const classNames = [styles['dialog']];
	if (isOpen) {
		classNames.push(styles['open']);
	}
	if (config.app.auth.enabled === false) {
		return null;
	}

	if (!config.app.auth.type) {
		return null;
	}

	if (!validTypes.has(config.app.auth.type)) {
		return null;
	}

	let content = (
		<SignUp
			onCloseDialog={onCloseDialog}
			onClickLogIn={onClickLogIn}
			onSuccess={() => {
				setMode('login');
			}}
		/>
	);
	if (mode === 'login') {
		content = (
			<Login
				setMode={setMode}
				onCloseDialog={onCloseDialog}
				onClickSignUp={onClickSignUp}
				onSuccess={onSuccess}
			/>
		);
	} /*else if (mode === 'forgot') {
		content = (
			<ForgotPassword
				setMode={setMode}
				onCloseDialog={onCloseDialog}
				onSuccess={onSuccess}
			/>
		);
	}*/
	return <div className={classNames.join(' ')}>{content}</div>;
};

export function isUserAuthenticated(): boolean {
	return !!localStorage.getItem('authenticated');
}

interface DialogProps {
	isOpen: boolean;
	mode: ModeType;
	setMode: (mode: ModeType) => void;
	onClickSignUp: () => void;
	onClickLogIn: () => void;
	onCloseDialog: () => void;
	onSuccess: () => void;
}

export type ModeType = 'login' | 'signup' | 'forgot';

const Login = ({ onClickSignUp, onCloseDialog, onSuccess }: LoginProps) => {
	const { sendEvent } = useContext(TrackerContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const ref = useRef(null);
	useOnClickOutside(ref, () => {
		onCloseDialog();
	});
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);
		authenticateUser({ username, password })
			.then(() => {
				sendEvent({
					type: 'LOGIN',
				});
				onSuccess();
			})
			.catch((e) => {
				alert(e.message);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	let registerBtn = null;
	if (config?.build?.links?.register) {
		registerBtn = (
			<>
				<p>Don&apos;t have an account? </p>
				<button onClick={onClickSignUp}>Register</button>
			</>
		);
	}

	return (
		<form ref={ref} onSubmit={onSubmit}>
			<div className={styles['details']}>
				<h3>Log in to your account</h3>
				<div className={styles['input-control']}>
					<label htmlFor="email-input-1">Username</label>
					<input
						type="text"
						id="email-input-1"
						value={username}
						onChange={(e) => {
							setUsername(e.target.value);
						}}
						name="username"
						required
					/>
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="password-input-1">Password</label>
					<input
						type="password"
						id="password-input-1"
						name="password"
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
						}}
						required
					/>
				</div>
				<div className={styles['remember-container']}>
					<div>
						{/*
						<a
							rel="noreferrer"
							href=""
							target="_blank"
						>
							Forgot password?
						</a>
						*/}
					</div>
				</div>
			</div>
			<div className={styles['control']}>
				{isLoading ? (
					<button className={styles['loading']} disabled>
						<div />
					</button>
				) : (
					<button>Sign In</button>
				)}
				<div>{registerBtn}</div>
			</div>
		</form>
	);
};

interface LoginProps {
	onClickSignUp: () => void;
	onCloseDialog: () => void;
	setMode: (mode: ModeType) => void;
	onSuccess: () => void;
}

async function authenticateUser(
	args: LoginArgs,
): Promise<AuthenticateUserResponse> {
	if (config?.app?.auth?.type === 'jwt') {
		return authenticateViaJwt(args);
	}

	return authenticateViaLogin(args);
}

const SignUp = (args: SignUpProps) => {
	const { onClickLogIn, onCloseDialog, onSuccess } = args;
	const ref = useRef(null);
	useOnClickOutside(ref, () => {
		onCloseDialog();
	});
	if (config?.app?.auth.type === 'cognito') {
		return <SignUpCognito {...args} />;
	}
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSuccess();
	};
	return (
		<form ref={ref} onSubmit={onSubmit}>
			<div className={styles['details']}>
				<h3>Create an account</h3>
				<h4>Start right now with us</h4>
				<div className={styles['input-control']}>
					<label htmlFor="name-input">Name</label>
					<input type="text" id="name-input" name="name" required />
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="email-input">Username</label>
					<input
						type="text"
						id="email-input"
						name="username"
						required
					/>
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="password-input">Password</label>
					<input
						minLength={8}
						type="password"
						id="password-input"
						name="password"
						required
					/>
				</div>
			</div>
			<div className={styles['control']}>
				<button>Sign Up</button>
				<div>
					<p>Already have an account?</p>
					<button onClick={onClickLogIn}>Sign In</button>
				</div>
			</div>
		</form>
	);
};
